import { Box, Checkbox, Flex, Heading, HStack, Stack, Tag, Text, Textarea } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { ContributingEntityCommitmentDto } from "../../../../autogen/bff-api";
import type { ContributionAudience } from "./ContributionWizard";
import { StatusAccentBar } from "../../../../common/components/StatusAccentBar";
import { StatusDotLabel } from "../../../../common/components/StatusDotLabel";
import type { StatusVariant } from "../../../../common/components/StatusVariant";

interface Props {
  leadSupplierName: string;
  commitment: ContributingEntityCommitmentDto | null | undefined;
  onChange: (next: ContributingEntityCommitmentDto) => void;
  readOnly: boolean;
  audience: ContributionAudience;
}

export const ContributingCommitmentCard = ({
  leadSupplierName,
  commitment,
  onChange,
  readOnly,
  audience,
}: Props) => {
  const { t } = useTranslation();

  const confirmed = commitment?.confirmed ?? false;
  const resources = commitment?.resources ?? "";
  const variant: StatusVariant = confirmed ? "ok" : "neutral";

  return (
    <Box mb="8">
      <HStack spacing="2" align="center" flexWrap="wrap" mb="2">
        <Heading size="md">{t("Commitment of resources")}</Heading>
        <Tag size="sm" bg="smPrimarySofter" color="smPrimary" textTransform="none">
          {t("FOA § 16-10")}
        </Tag>
      </HStack>
      <Text fontSize="sm" color="smMuted" mb="4">
        {audience === "lead"
          ? t(
              "{{leadSupplier}} relies on this company's capacity to meet the qualification requirements. The company must therefore undertake that the resources really will be at your disposal for this contract.",
              { leadSupplier: leadSupplierName },
            )
          : t(
              "{{leadSupplier}} relies on your capacity to meet the qualification requirements. You must therefore undertake that the resources really will be at their disposal for this contract.",
              { leadSupplier: leadSupplierName },
            )}
      </Text>

      <Box position="relative" overflow="hidden" borderWidth="1px" borderColor="smBorder" rounded="lg" bg="smBackground">
        <StatusAccentBar variant={variant} />
        <Box px="4" py="3">
          <Stack spacing="4">
            <Flex justify="space-between" align="flex-start" gap="3">
              <Checkbox
                isDisabled={readOnly}
                isChecked={confirmed}
                onChange={(e) => onChange({ confirmed: e.target.checked, resources })}
              >
                <Text fontSize="sm">
                  {audience === "lead"
                    ? t(
                        "The company undertakes to place the necessary resources at {{leadSupplier}}'s disposal for the execution of this contract.",
                        { leadSupplier: leadSupplierName },
                      )
                    : t(
                        "We undertake to place the necessary resources at {{leadSupplier}}'s disposal for the execution of this contract.",
                        { leadSupplier: leadSupplierName },
                      )}
                </Text>
              </Checkbox>
              <StatusDotLabel
                variant={variant}
                label={confirmed ? t("Confirmed") : t("Not confirmed")}
              />
            </Flex>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb="2">
                {audience === "lead"
                  ? t("Which resources is the company providing?")
                  : t("Which resources are you providing?")}
              </Text>
              <Textarea
                rows={3}
                isReadOnly={readOnly}
                value={resources}
                placeholder={String(t("For example personnel, equipment, facilities or financial capacity."))}
                onChange={(e) => onChange({ confirmed, resources: e.target.value })}
              />
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
